import { cloneDeep } from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import './image-marker.less';
import { calculateMarkerPosition } from './utils';

const DEFAULT_BUFFER = 12;
const INITIAL_SCALE = 1.0;

const ImageAreaMarker = ({
  src,
  markers,
  areaMarkers: areaMarkersProp,
  onAddMarker,
  markerComponent: MarkerComponent,
  areaMarkerComponent: AreaMarkerComponent,
  bufferLeft = DEFAULT_BUFFER,
  bufferTop = DEFAULT_BUFFER,
  alt = 'Markers',
  extraClass = '',
  onAreaMarkersChange,
}) => {
  const imageRef = useRef(null);
  const imageMarkerContainerAreaMapRef = useRef(null);
  const imageMapRef = useRef(null);
  const resetTransformRef = useRef(null);
  const [areaMarkers, setAreaMarkers] = React.useState([]);
  const [isDisableDragging, setIsDisableDragging] = useState(false);
  const [currentScaleValue, setCurrentScaleValue] = useState(INITIAL_SCALE);
  const [selectedAreaMarkerId, setSelectedAreaMarkerId] = useState(null);
  const [isImageCentered, setIsImageCentered] = useState(false);
  const [computedInitialScale, setComputedInitialScale] = useState(INITIAL_SCALE);

  useEffect(() => {
    setAreaMarkers(cloneDeep(areaMarkersProp));
  }, [areaMarkersProp]);

  useEffect(() => {
    if (imageMarkerContainerAreaMapRef.current) {
      imageMarkerContainerAreaMapRef.current.addEventListener('click', () => {
        setSelectedAreaMarkerId(null);
      });
    }
  }, []);

  // Handle window resize to recalculate scale
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      resizeTimeout = setTimeout(() => {
        if (imageRef.current && imageMarkerContainerAreaMapRef.current) {
          const newScale = computeInitialScale();
          setComputedInitialScale(newScale);
          
          // Reset transform with new scale
          if (resetTransformRef.current && resetTransformRef.current.centerView) {
            resetTransformRef.current.centerView(newScale, 0);
          }
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  const updateAreaMarker = ({ area_id, newRectData }) => {
    const findAreaMarker = areaMarkers.find(
      areaMarker => areaMarker.area_id === area_id
    );
    if (findAreaMarker) {
      const newTopPercentValue = +newRectData.topPercent;
      const newLeftPercentValue = +newRectData.leftPercent;
      const newWidthPercentage = +newRectData.widthPercent;
      const newHeightPercentage = +newRectData.heightPercent;
      findAreaMarker['newRect'] = {
        top: newTopPercentValue,
        left: newLeftPercentValue,
        width: newWidthPercentage,
        height: newHeightPercentage,
      };
    } else {
      console.warn('AreaMarker not found');
    }
    setAreaMarkers([...areaMarkers]);
    onAreaMarkersChange({ areaList: areaMarkers });
  };

  const updateAreaMarkerStatus = ({ status }) => {
    if (status === 'dragging' || status === 'resizing') {
      setIsDisableDragging(true);
    } else {
      setIsDisableDragging(false);
    }
  };

  const updateSelectedAreaMarkerId = ({ area_id }) => {
    setSelectedAreaMarkerId(area_id);
  };

  const computeInitialScale = () => {
    if (!imageRef.current || !imageMarkerContainerAreaMapRef.current) {
      return INITIAL_SCALE;
    }

    const image = imageRef.current;
    const container = imageMarkerContainerAreaMapRef.current;
    
    const imageWidth = image.naturalWidth;
    const imageHeight = image.naturalHeight;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    if (
      imageWidth === 0 ||
      imageHeight === 0 ||
      containerWidth === 0 ||
      containerHeight === 0
    ) {
      return INITIAL_SCALE;
    }

    // Calculate scale to fit image within container while maintaining aspect ratio
    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;

    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond original size

    // Add some padding (10% margin)
    return Math.max(scale * 0.9, 0.8); // Ensure it's not smaller than 0.8
  };

  const handleImageLoad = () => {
    // Compute the appropriate initial scale based on image and container dimensions
    const newScale = computeInitialScale();
    setComputedInitialScale(newScale);
    
    setTimeout(() => {
      if (resetTransformRef.current && resetTransformRef.current.centerView) {
        // Set the scale and center the image
        resetTransformRef.current.centerView(newScale, 0);
      }
      setIsImageCentered(true);
    }, 100);
  };

  const handleImageClick = event => {
    if (!imageRef.current || !onAddMarker) {
      return;
    }
    const imageDimentions = imageRef.current.getBoundingClientRect();

    const [top, left] = calculateMarkerPosition(
      event,
      imageDimentions,
      window.scrollY,
      bufferLeft,
      bufferTop
    );

    onAddMarker({
      top,
      left,
    });
  };

  const getItemPosition = marker => {
    const { top, left } = marker;
    return {
      top: `${top}%`,
      left: `${left}%`,
    };
  };

  const getAreaPosition = areaMarker => {
    const { rect, newRect } = areaMarker;
    if (newRect) {
      return {
        top: `${newRect.top}%`,
        left: `${newRect.left}%`,
        width: `${newRect.width}%`,
        height: `${newRect.height}%`,
      };
    } else if (rect) {
      return {
        top: `${rect.top}%`,
        left: `${rect.left}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`,
      };
    }
    return {
      top: `${0}%`,
      left: `${0}%`,
      width: `${0}%`,
      height: `${0}%`,
    };
  };

  return (
    <TransformWrapper
      ref={imageMapRef}
      wheel={{ step: 0.1, disabled: true }}
      minScale={0.2}
      maxScale={2.0}
      centerOnInit={true}
      limitToBounds={true}
      initialScale={computedInitialScale}
      panning={{ disabled: false }}
      disabled={isDisableDragging}
      onTransformed={(ref, event) => {
        setCurrentScaleValue(ref.state.scale);
      }}
    >
      {({ zoomIn, zoomOut, resetTransform, centerView, ...rest }) => {
        // Store resetTransform function in ref for use in image onLoad
        resetTransformRef.current = {
          zoomIn,
          zoomOut,
          resetTransform,
          centerView
        };

        return (
          <React.Fragment>
            <div className="zoom-controls">
              <button
                className="zoom-button zoom-out"
                onClick={() => zoomOut()}
                type="button"
              >
                <img src="/assets/img/zoom-out-icon.svg" alt="Zoom Out" />
              </button>
              <button
                className="zoom-button zoom-fit"
                onClick={() => resetTransform()}
                type="button"
              >
                <img src="/assets/img/zoom-fit-icon.svg" alt="Zoom Fit" />
              </button>
              <button
                className="zoom-button zoom-in"
                onClick={() => zoomIn()}
                type="button"
              >
                <img src="/assets/img/zoom-in-icon.svg" alt="Zoom In" />
              </button>
            </div>
            <TransformComponent
              wrapperStyle={{
                marginLeft: 'auto',
                marginRight: 'auto',
                width: '100%',
                height: '100%',
              }}
              contentStyle={{
                // marginLeft: 'auto',
                // marginRight: 'auto',
              }}
            >
              <div
                className="image-marker"
                id={'imageMarkerContainerAreaMap'}
                ref={imageMarkerContainerAreaMapRef}
                style={{ visibility: isImageCentered ? 'visible' : 'hidden' }}
              >
                <div>
                  <img
                    src={src}
                    alt={alt}
                    onClick={handleImageClick}
                    onLoad={handleImageLoad}
                    className={'image-marker__image ' + extraClass}
                    ref={imageRef}
                  />
                </div>
                {markers.map((marker, itemNumber) => (
                  <div
                    className={`image-marker__marker ${!MarkerComponent && 'image-marker__marker--default'
                      }`}
                    key={itemNumber}
                    style={{
                      ...getItemPosition(marker),
                      cursor: 'pointer',
                      zIndex: 902,
                    }}
                    data-testid="marker"
                  >
                    {MarkerComponent ? (
                      <MarkerComponent {...marker} itemNumber={itemNumber} />
                    ) : (
                      itemNumber + 1
                    )}
                  </div>
                ))}
                {areaMarkers.map((areaMarker, itemNumber) => (
                  <div
                    className={`image-marker__marker area-marker__marker ${!AreaMarkerComponent && 'image-marker__marker--default'
                      }`}
                    key={`area-marker-${itemNumber}`}
                    style={{
                      ...getAreaPosition(areaMarker),
                      cursor: 'pointer',
                      zIndex: 900,
                    }}
                    data-testid="marker"
                  >
                    {AreaMarkerComponent ? (
                      <AreaMarkerComponent
                        {...areaMarker}
                        newRect={areaMarker.newRect}
                        // eslint-disable-next-line eqeqeq
                        isSelected={selectedAreaMarkerId == areaMarker.area_id}
                        itemNumber={itemNumber}
                        imageDimension={imageRef}
                        updateAreaMarker={updateAreaMarker}
                        updateAreaMarkerStatus={updateAreaMarkerStatus}
                        updateSelectedAreaMarkerId={updateSelectedAreaMarkerId}
                        scaleValue={currentScaleValue}
                      />
                    ) : (
                      itemNumber + 1
                    )}
                  </div>
                ))}
              </div>
            </TransformComponent>
          </React.Fragment>
        );
      }}
    </TransformWrapper>
  );
};

export default ImageAreaMarker;
